import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { 
  BarChart3, BookOpen, CheckCircle, Star, Trophy, Award, Flame, 
  ChevronDown, ChevronUp, Calendar, MessageSquare, PenLine, Gift,
  ArrowLeft, Users, Loader, RefreshCw, Image, FileText, Heart
} from "lucide-react";
import { AvatarDisplay } from "./ParentDashboard";

export default function AnalyticsDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [expandedChild, setExpandedChild] = useState(null);
  const [activeTab, setActiveTab] = useState("overview"); // overview, answers, drawings, rewards

  useEffect(() => { loadAnalytics(); }, []);

  const loadAnalytics = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/analytics/dashboard`);
      setData(res.data);
      if (res.data.children?.length > 0) {
        setExpandedChild(res.data.children[0].child.id);
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load analytics");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#F0F4FF" }}>
        <div className="text-center">
          <Loader size={48} className="animate-spin mx-auto mb-4 text-[#118AB2]" />
          <p className="font-bold text-[#073B4C]">Loading Analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "#F0F4FF" }}>
        <div className="bg-white rounded-3xl p-8 text-center max-w-sm shadow-lg">
          <p className="text-red-500 font-bold mb-4">{error}</p>
          <button onClick={loadAnalytics} className="bg-[#118AB2] text-white font-bold px-6 py-3 rounded-xl">
            <RefreshCw size={16} className="inline mr-2" /> Retry
          </button>
        </div>
      </div>
    );
  }

  const selectedChild = data?.children?.find(c => c.child.id === expandedChild);

  return (
    <div className="min-h-screen pb-8" style={{ background: "#F0F4FF" }}>
      {/* Header */}
      <div className="px-4 pt-6 pb-8" style={{ background: "linear-gradient(135deg, #073B4C 0%, #118AB2 100%)" }}>
        <div className="max-w-4xl mx-auto">
          <button onClick={() => navigate("/parent")} 
            className="flex items-center gap-2 text-white/70 hover:text-white mb-4 transition-colors"
            data-testid="back-to-dashboard">
            <ArrowLeft size={20} /> Back to Dashboard
          </button>
          <div className="flex items-center gap-3">
            <div className="bg-white/20 rounded-2xl p-3">
              <BarChart3 size={28} color="white" strokeWidth={2.5} />
            </div>
            <div>
              <h1 className="text-2xl font-black text-white" style={{ fontFamily: "Fredoka, sans-serif" }}>
                Family Analytics
              </h1>
              <p className="text-white/70 text-sm">Track your children's progress and achievements</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 -mt-4">
        {/* Family Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <SummaryCard 
            icon={BookOpen} 
            label="Total Lessons" 
            value={data?.children?.reduce((sum, c) => sum + (c.stats.total_lessons_completed ?? c.stats.lessons ?? 0), 0) || data?.total_lessons || 0}
            gradient={["#118AB2", "#06D6A0"]}
          />
          <SummaryCard 
            icon={CheckCircle} 
            label="Tasks Done" 
            value={data?.children?.reduce((sum, c) => sum + (c.stats.total_tasks_completed ?? c.stats.tasks ?? 0), 0) || data?.total_tasks || 0}
            gradient={["#9B5DE5", "#EF476F"]}
          />
          <SummaryCard 
            icon={Users} 
            label="Family Studies" 
            value={data?.family_studies?.completed ?? 0}
            gradient={["#F77F00", "#FFD166"]}
          />
          <SummaryCard 
            icon={Star} 
            label="Total Stars" 
            value={data?.children?.reduce((sum, c) => sum + (c.stats.total_stars ?? c.stats.stars ?? 0), 0) || data?.total_stars || 0}
            gradient={["#FFD166", "#F77F00"]}
          />
        </div>

        {/* Child Selector */}
        <div className="bg-white rounded-3xl shadow-lg mb-6 overflow-hidden">
          <div className="p-4 border-b" style={{ background: "linear-gradient(90deg, #073B4C, #118AB2)" }}>
            <h2 className="font-black text-white" style={{ fontFamily: "Fredoka, sans-serif" }}>
              Select Child
            </h2>
          </div>
          <div className="p-4 flex flex-wrap gap-2">
            {data?.children?.map((c) => (
              <button
                key={c.child.id}
                onClick={() => setExpandedChild(c.child.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full font-bold text-sm transition-all ${
                  expandedChild === c.child.id 
                    ? 'bg-[#118AB2] text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                data-testid={`select-child-${c.child.id}`}
              >
                <AvatarDisplay avatar={c.child.avatar} size={28} />
                {c.child.name}
              </button>
            ))}
          </div>
        </div>

        {/* Selected Child Analytics */}
        {selectedChild && (
          <div className="space-y-6">
            {/* Child Stats Overview */}
            <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
              <div className="p-4" style={{ background: "linear-gradient(135deg, #118AB2, #06D6A0)" }}>
                <div className="flex items-center gap-3">
                  <AvatarDisplay avatar={selectedChild.child.avatar} size={48} />
                  <div>
                    <h2 className="font-black text-white text-xl" style={{ fontFamily: "Fredoka, sans-serif" }}>
                      {selectedChild.child.name}'s Progress
                    </h2>
                    <p className="text-white/80 text-sm">Age {selectedChild.child.age}</p>
                  </div>
                </div>
              </div>
              
              <div className="p-4 grid grid-cols-2 md:grid-cols-5 gap-4">
                <StatBox icon={BookOpen} label="Lessons" value={selectedChild.stats.total_lessons_completed || selectedChild.stats.lessons || 0} color="#118AB2" />
                <StatBox icon={CheckCircle} label="Tasks" value={selectedChild.stats.total_tasks_completed || selectedChild.stats.tasks || 0} color="#06D6A0" />
                <StatBox icon={Star} label="Stars" value={selectedChild.stats.total_stars || selectedChild.stats.stars || 0} color="#FFD166" />
                <StatBox icon={Flame} label="Streak" value={`${selectedChild.stats.current_streak ?? selectedChild.stats.streak ?? 0} days`} color="#EF476F" />
                <StatBox icon={Trophy} label="Badges" value={selectedChild.stats.badges_count ?? selectedChild.stats.badges ?? 0} color="#9B5DE5" />
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="flex gap-2 overflow-x-auto pb-2">
              {[
                { id: "overview", label: "Overview", icon: BarChart3 },
                { id: "answers", label: "Written Answers", icon: MessageSquare },
                { id: "drawings", label: "Drawings", icon: Image },
                { id: "rewards", label: "Rewards", icon: Gift },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full font-bold text-sm whitespace-nowrap transition-all ${
                    activeTab === tab.id
                      ? 'bg-[#073B4C] text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <tab.icon size={16} />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            {activeTab === "overview" && (
              <OverviewTab child={selectedChild} />
            )}
            {activeTab === "answers" && (
              <AnswersTab child={selectedChild} />
            )}
            {activeTab === "drawings" && (
              <DrawingsTab child={selectedChild} />
            )}
            {activeTab === "rewards" && (
              <RewardsTab child={selectedChild} />
            )}
          </div>
        )}

        {/* Parent Notes Section */}
        {data?.parent_notes?.length > 0 && (
          <div className="bg-white rounded-3xl shadow-lg mt-6 overflow-hidden">
            <div className="p-4 border-b" style={{ background: "linear-gradient(90deg, #EF476F, #FFD166)" }}>
              <div className="flex items-center gap-2">
                <Heart size={20} color="white" fill="white" />
                <h3 className="font-black text-white" style={{ fontFamily: "Fredoka, sans-serif" }}>
                  Your Notes to Children
                </h3>
              </div>
            </div>
            <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
              {data.parent_notes.slice(0, 20).map((note, i) => (
                <div key={i} className="bg-gray-50 rounded-xl p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full text-white ${
                      note.note_type === "encouragement" ? "bg-green-500" :
                      note.note_type === "prayer" ? "bg-purple-500" : "bg-blue-500"
                    }`}>
                      {note.note_type}
                    </span>
                    <span className="text-xs text-gray-400">
                      {new Date(note.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700">"{note.content}"</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, gradient }) {
  return (
    <div className="bg-white rounded-2xl p-4 shadow-md">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-2"
        style={{ background: `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})` }}>
        <Icon size={20} color="white" strokeWidth={2.5} />
      </div>
      <p className="text-2xl font-black text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
        {value}
      </p>
      <p className="text-xs text-gray-500 font-medium">{label}</p>
    </div>
  );
}

function StatBox({ icon: Icon, label, value, color }) {
  return (
    <div className="text-center p-3 rounded-2xl" style={{ background: `${color}15` }}>
      <Icon size={24} color={color} className="mx-auto mb-1" />
      <p className="text-xl font-black text-[#073B4C]">{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
    </div>
  );
}

function OverviewTab({ child }) {
  return (
    <div className="space-y-4">
      {/* Completed Lessons */}
      <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
        <div className="p-4 border-b bg-gradient-to-r from-[#118AB2] to-[#06D6A0]">
          <div className="flex items-center gap-2">
            <BookOpen size={20} color="white" />
            <h3 className="font-black text-white">Completed Lessons ({child.completed_lessons?.length || 0})</h3>
          </div>
        </div>
        <div className="p-4 space-y-2 max-h-80 overflow-y-auto">
          {child.completed_lessons?.length > 0 ? (
            child.completed_lessons.map((lesson, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <CheckCircle size={20} className="text-green-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm text-[#073B4C] truncate">{lesson.lesson_title}</p>
                  <p className="text-xs text-gray-500">{lesson.verse_reference} • {lesson.date}</p>
                </div>
                {lesson.quiz_result && (
                  <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-1 rounded-full">
                    Quiz: {lesson.quiz_result.correct_count}/{lesson.quiz_result.total}
                  </span>
                )}
              </div>
            ))
          ) : (
            <p className="text-gray-500 text-center py-4">No lessons completed yet</p>
          )}
        </div>
      </div>

      {/* Badges */}
      <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
        <div className="p-4 border-b bg-gradient-to-r from-[#9B5DE5] to-[#EF476F]">
          <div className="flex items-center gap-2">
            <Trophy size={20} color="white" />
            <h3 className="font-black text-white">Badges Earned ({child.badges?.length || 0})</h3>
          </div>
        </div>
        <div className="p-4">
          {child.badges?.length > 0 ? (
            <div className="flex flex-wrap gap-3">
              {child.badges.map((badge, i) => (
                <div key={i} className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 text-center min-w-[100px]">
                  <Award size={32} className="mx-auto mb-1 text-yellow-500" />
                  <p className="text-xs font-bold text-[#073B4C]">{badge.badge_type?.replace(/_/g, ' ')}</p>
                  <p className="text-xs text-gray-400">{new Date(badge.earned_at).toLocaleDateString()}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No badges earned yet</p>
          )}
        </div>
      </div>
    </div>
  );
}

function AnswersTab({ child }) {
  const [expandedAnswer, setExpandedAnswer] = useState(null);
  
  const allAnswers = [
    ...(child.recent_answers || []).map(a => ({ ...a, type: "lesson" })),
    ...(child.task_responses || []).filter(t => t.answer_content).map(t => ({ 
      ...t, 
      type: "task",
      content: t.answer_content,
      created_at: t.answer_updated_at || t.created_at
    }))
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  return (
    <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
      <div className="p-4 border-b bg-gradient-to-r from-[#118AB2] to-[#4CC9F0]">
        <div className="flex items-center gap-2">
          <MessageSquare size={20} color="white" />
          <h3 className="font-black text-white">Written Answers & Reflections ({allAnswers.length})</h3>
        </div>
      </div>
      <div className="p-4 space-y-3 max-h-[600px] overflow-y-auto">
        {allAnswers.length > 0 ? (
          allAnswers.map((answer, i) => (
            <div 
              key={i} 
              className={`border-2 rounded-2xl overflow-hidden transition-all cursor-pointer ${
                expandedAnswer === i ? 'border-[#118AB2]' : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => setExpandedAnswer(expandedAnswer === i ? null : i)}
            >
              {/* Header - Always visible */}
              <div className="p-3 flex items-center justify-between" 
                style={{ background: answer.type === "lesson" ? "#E0F4FF" : "#F0E6FF" }}>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full text-white ${
                    answer.type === "lesson" ? "bg-[#118AB2]" : "bg-[#9B5DE5]"
                  }`}>
                    {answer.type === "lesson" ? "Lesson" : "Activity"}
                  </span>
                  <span className="text-sm font-bold text-[#073B4C] truncate max-w-[200px]">
                    {answer.lesson_title || "Response"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">
                    {new Date(answer.created_at).toLocaleDateString()}
                  </span>
                  <ChevronDown 
                    size={18} 
                    className={`text-gray-400 transition-transform ${expandedAnswer === i ? 'rotate-180' : ''}`}
                  />
                </div>
              </div>
              
              {/* Expanded Content */}
              <div className={`transition-all duration-300 ${expandedAnswer === i ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
                <div className="p-4 bg-white">
                  {answer.prompt && (
                    <div className="mb-3 p-3 bg-blue-50 rounded-xl">
                      <p className="text-xs font-bold text-[#118AB2] mb-1">Question:</p>
                      <p className="text-sm text-gray-700 italic">{answer.prompt}</p>
                    </div>
                  )}
                  <div className="p-3 bg-gray-50 rounded-xl">
                    <p className="text-xs font-bold text-gray-500 mb-1">Answer:</p>
                    <p className="text-sm text-[#073B4C] leading-relaxed">"{answer.content}"</p>
                  </div>
                  <p className="text-xs text-gray-400 mt-2 text-right">
                    {new Date(answer.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
              
              {/* Preview when collapsed */}
              {expandedAnswer !== i && (
                <div className="px-3 pb-3">
                  <p className="text-sm text-gray-600 line-clamp-2">"{answer.content}"</p>
                </div>
              )}
            </div>
          ))
        ) : (
          <p className="text-gray-500 text-center py-8">No written answers yet</p>
        )}
      </div>
    </div>
  );
}

function DrawingsTab({ child }) {
  const [expandedDrawing, setExpandedDrawing] = useState(null);
  
  const allDrawings = [
    ...(child.recent_drawings || []).map(d => ({ ...d, type: "lesson" })),
    ...(child.task_responses || []).filter(t => t.drawing_data).map(t => ({ 
      ...t, 
      type: "task",
      drawing_data: t.drawing_data
    }))
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  return (
    <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
      <div className="p-4 border-b bg-gradient-to-r from-[#9B5DE5] to-[#EF476F]">
        <div className="flex items-center gap-2">
          <Image size={20} color="white" />
          <h3 className="font-black text-white">Drawings ({allDrawings.length})</h3>
        </div>
      </div>
      <div className="p-4">
        {allDrawings.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {allDrawings.map((drawing, i) => (
              <div 
                key={i} 
                className={`rounded-2xl overflow-hidden cursor-pointer transition-all ${
                  expandedDrawing === i 
                    ? 'col-span-2 md:col-span-3 border-4 border-[#9B5DE5]' 
                    : 'border-2 border-gray-200 hover:border-[#9B5DE5] hover:shadow-lg'
                }`}
                onClick={() => setExpandedDrawing(expandedDrawing === i ? null : i)}
              >
                {/* Header */}
                <div className="p-2 flex items-center justify-between" 
                  style={{ background: drawing.type === "lesson" ? "#F0E6FF" : "#FFE0EB" }}>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full text-white ${
                    drawing.type === "lesson" ? "bg-[#9B5DE5]" : "bg-[#EF476F]"
                  }`}>
                    {drawing.type === "lesson" 
                      ? (drawing.activity_type === "fun_idea" ? "Fun Idea" : "Lesson") 
                      : "Activity"}
                  </span>
                  <span className="text-xs text-gray-500">
                    {new Date(drawing.created_at).toLocaleDateString()}
                  </span>
                </div>
                
                {/* Drawing Image */}
                <div className={`p-2 bg-white ${expandedDrawing === i ? '' : 'aspect-square'}`}>
                  <img 
                    src={drawing.drawing_data} 
                    alt="Child's drawing"
                    className={`rounded-lg border border-gray-200 ${
                      expandedDrawing === i 
                        ? 'w-full max-h-[500px] object-contain' 
                        : 'w-full h-full object-cover'
                    }`}
                  />
                </div>
                
                {/* Expanded details */}
                {expandedDrawing === i && (
                  <div className="p-3 bg-gray-50 border-t">
                    <p className="text-xs text-gray-500">
                      Created: {new Date(drawing.created_at).toLocaleString()}
                    </p>
                    <p className="text-xs text-[#9B5DE5] mt-1 font-bold">
                      Click to collapse
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">No drawings yet</p>
        )}
      </div>
    </div>
  );
}

function RewardsTab({ child }) {
  return (
    <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
      <div className="p-4 border-b bg-gradient-to-r from-[#FFD166] to-[#F77F00]">
        <div className="flex items-center gap-2">
          <Gift size={20} color="white" />
          <h3 className="font-black text-white">Rewards History ({child.rewards?.length || 0})</h3>
        </div>
      </div>
      <div className="p-4 space-y-3 max-h-[500px] overflow-y-auto">
        {child.rewards?.length > 0 ? (
          child.rewards.map((reward, i) => (
            <div key={i} className={`rounded-xl p-4 ${
              reward.claimed ? "bg-green-50 border-2 border-green-200" : "bg-gray-50"
            }`}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-bold text-[#073B4C]">{reward.reward_name || "Weekly Reward"}</span>
                {reward.claimed ? (
                  <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                    <CheckCircle size={12} /> Claimed
                  </span>
                ) : (
                  <span className="bg-gray-300 text-gray-600 text-xs font-bold px-2 py-1 rounded-full">
                    Not Claimed
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600 mb-2">{reward.reward_description || "Complete the weekly goal"}</p>
              <div className="flex items-center justify-between text-xs text-gray-400">
                <span>Week of {reward.week_start}</span>
                <span>Goal: {reward.lessons_completed || 0}/{reward.lessons_goal || 7} lessons</span>
              </div>
            </div>
          ))
        ) : (
          <p className="text-gray-500 text-center py-8">No rewards history yet</p>
        )}
      </div>
    </div>
  );
}
